import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDarkMode } from '../App';
import { useContext } from 'react';
import { SocketContext } from '../contexts/SocketContext';
import { useNotifications } from '../contexts/NotificationContext';
import NotificationPermissionRequest from './NotificationPermissionRequest';
import MobilePlatformAdTicker from './MobilePlatformAdTicker';
import axios from 'axios';
import { getImageUrl } from '../utils/imageUrl';
import { API_BASE_URL, apiUrl } from '../config/api';
import { BRAND_LOGO_PUBLIC_PATH } from '../config/branding';
import { regionDisplayName, OA_COUNTRY_STORAGE_KEY } from '../utils/countryDisplay';
import { localizeStoredNotification } from '../utils/storedNotificationI18n';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGS } from '../i18n/config';
import { 
  Search, 
  User, 
  MessageSquare, 
  LogOut, 
  Plus,
  Menu,
  X,
  Settings,
  Moon,
  Sun,
  Bell,
  Globe,
  Power,
  LogIn,
  Home
} from 'lucide-react';
import { handleCloseApp, shouldShowCloseAppButton } from '../utils/closeApp';

const Header: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [showCloseApp, setShowCloseApp] = useState(false);
  const searchQuery = searchParams.get('search') ?? '';
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [isHovering, setIsHovering] = useState(false);
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false);
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification, fetchNotifications } = useNotifications();
  
  const socketContext = useContext(SocketContext);
  const socket = socketContext?.socket;

  const [auctionCountries, setAuctionCountries] = useState<string[]>([]);
  const [storedCountryFilter, setStoredCountryFilter] = useState('');
  const countryHydrateDoneRef = useRef(false);

  // Live Search Suggestions
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const isHomePath = location.pathname === '/';

  useEffect(() => {
    const updateCloseAppVisibility = () => setShowCloseApp(shouldShowCloseAppButton());
    updateCloseAppVisibility();
    window.addEventListener('resize', updateCloseAppVisibility);
    return () => window.removeEventListener('resize', updateCloseAppVisibility);
  }, []);

  useEffect(() => {
    if (!isMobileNavOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobileNavOpen]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get<{
          data?: { countries?: string[] };
          countries?: string[];
        }>(`${API_BASE_URL}/auctions/countries`, { timeout: 12000 });
        const countries =
          res.data?.data?.countries ?? res.data?.countries;
        if (!cancelled) {
          setAuctionCountries(
            Array.isArray(countries) && countries.length > 0 ? countries : ['JO']
          );
        }
      } catch {
        if (!cancelled) setAuctionCountries(['JO']);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    try {
      const s = localStorage.getItem(OA_COUNTRY_STORAGE_KEY);
      setStoredCountryFilter(s && /^[A-Za-z]{2}$/.test(s) ? s.toUpperCase() : '');
    } catch {
      setStoredCountryFilter('');
    }
  }, [isHomePath, searchParams]);

  /** مرة واحدة لكل زيارة للرئيسية: استعادة الدولة من التخزين إن لم تكن في الرابط */
  useEffect(() => {
    if (!isHomePath) {
      countryHydrateDoneRef.current = false;
      return;
    }
    if (countryHydrateDoneRef.current) return;
    const fromUrl = searchParams.get('country');
    if (fromUrl) {
      countryHydrateDoneRef.current = true;
      return;
    }
    countryHydrateDoneRef.current = true;
    try {
      const raw = localStorage.getItem(OA_COUNTRY_STORAGE_KEY);
      if (raw && /^[A-Za-z]{2}$/.test(raw)) {
        setSearchParams(
          (prev) => {
            const p = new URLSearchParams(prev);
            p.set('country', raw.toUpperCase());
            return p;
          },
          { replace: true }
        );
      }
    } catch {
      /* ignore */
    }
  }, [isHomePath, searchParams, setSearchParams]);

  const headerCountryValue = isHomePath ? (searchParams.get('country') || '') : storedCountryFilter;

  const applyCountryFilter = (code: string) => {
    const v = code.trim().toUpperCase();
    if (v && !/^[A-Z]{2}$/.test(v)) return;
    if (isHomePath) {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        if (v) {
          p.set('country', v);
          try {
            localStorage.setItem(OA_COUNTRY_STORAGE_KEY, v);
          } catch {
            /* ignore */
          }
        } else {
          p.delete('country');
          try {
            localStorage.removeItem(OA_COUNTRY_STORAGE_KEY);
          } catch {
            /* ignore */
          }
        }
        return p;
      });
    } else {
      if (v) {
        try {
          localStorage.setItem(OA_COUNTRY_STORAGE_KEY, v);
        } catch {
          /* ignore */
        }
        setStoredCountryFilter(v);
        navigate(`/?country=${encodeURIComponent(v)}`);
      } else {
        try {
          localStorage.removeItem(OA_COUNTRY_STORAGE_KEY);
        } catch {
          /* ignore */
        }
        setStoredCountryFilter('');
        navigate('/');
      }
    }
  };

  const handleSearchChange = (value: string) => {
    const trimmed = value.trim();
    if (location.pathname !== '/') {
      const p = new URLSearchParams();
      if (trimmed) p.set('search', trimmed);
      try {
        const c = localStorage.getItem(OA_COUNTRY_STORAGE_KEY);
        if (c && /^[A-Za-z]{2}$/.test(c)) p.set('country', c.toUpperCase());
      } catch {
        /* ignore */
      }
      const qs = p.toString();
      navigate(qs ? `/?${qs}` : '/');
      return;
    }
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (trimmed) p.set('search', trimmed);
      else p.delete('search');
      return p;
    });
  };

  // Debounced Search Suggestions
  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsSearching(true);
        const res = await axios.get(`${API_BASE_URL}/auctions/search-suggestions?q=${encodeURIComponent(searchQuery)}`);
        setSuggestions(res.data?.suggestions || []);
        setShowSuggestions(true);
      } catch (err) {
        console.error('Fetch suggestions error:', err);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // إخفاء القائمة المنسدلة عند النقر خارجها
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isMenuOpen && !target.closest('.user-menu-container')) {
        setIsMenuOpen(false);
      }
      if (isNotificationDropdownOpen && !target.closest('.notification-dropdown-container')) {
        setIsNotificationDropdownOpen(false);
      }
      if (showSuggestions && !target.closest('.search-container')) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen, isNotificationDropdownOpen, showSuggestions]);

  // إخفاء القائمة المنسدلة بعد تأخير عند رفع الماوس
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    if (!isHovering && isMenuOpen) {
      timeoutId = setTimeout(() => {
        setIsMenuOpen(false);
      }, 300); // تأخير 300ms قبل الإخفاء
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isHovering, isMenuOpen]);

  // تخفيف الطلبات: منع استدعاء متكرر خلال 2 ثانية
  const lastUnreadFetchRef = useRef(0);
  const lastNotificationsFetchRef = useRef(0);
  const THROTTLE_MS = 2000;

  const fetchUnreadMessages = useCallback(async () => {
    if (user?.id == null) {
      setUnreadMessages(0);
      return;
    }
    const now = Date.now();
    if (now - lastUnreadFetchRef.current < THROTTLE_MS) return;
    lastUnreadFetchRef.current = now;
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(apiUrl('/messages/unread-count'), {
        headers,
        timeout: 8000,
        skipNetworkErrorToast: true
      });
      setUnreadMessages(response.data.count ?? 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
      setUnreadMessages(0);
    }
  }, [user?.id]);

  const throttledFetchNotifications = useCallback(() => {
    const now = Date.now();
    if (now - lastNotificationsFetchRef.current < THROTTLE_MS) return;
    lastNotificationsFetchRef.current = now;
    fetchNotifications();
  }, [fetchNotifications]);

  // جعل الدالة متاحة عالمياً لتحديث العداد من صفحات أخرى
  useEffect(() => {
    (window as any).fetchUnreadMessages = fetchUnreadMessages;
    return () => {
      delete (window as any).fetchUnreadMessages;
    };
  }, [fetchUnreadMessages]);

  // الاستماع للرسائل الجديدة — تحديث فوري واحد عند الاتصال فقط
  useEffect(() => {
    if (!socket || user?.id == null) return;

    const handleNewMessage = () => fetchUnreadMessages();
    const handleMessageRead = () => fetchUnreadMessages();
    const handleNewNotification = () => throttledFetchNotifications();

    socket.on('new_message', handleNewMessage);
    socket.on('message_read', handleMessageRead);
    socket.on('new_notification', handleNewNotification);

    fetchUnreadMessages();

    return () => {
      socket.off('new_message', handleNewMessage);
      socket.off('message_read', handleMessageRead);
      socket.off('new_notification', handleNewNotification);
    };
  }, [socket, user?.id, fetchUnreadMessages, throttledFetchNotifications]);

  // جلب عند التحميل + تحديث دوري كل 60 ثانية فقط
  useEffect(() => {
    if (user?.id == null) {
      setUnreadMessages(0);
      return;
    }
    fetchUnreadMessages();
    const interval = setInterval(() => {
      fetchUnreadMessages();
      throttledFetchNotifications();
    }, 60000);
    return () => clearInterval(interval);
  }, [user?.id, fetchUnreadMessages, throttledFetchNotifications]);

  // الاستماع لتحديث عداد الرسائل غير المقروءة
  useEffect(() => {
    // console.log('👂 بدء الاستماع لتحديث عداد الرسائل غير المقروءة'); // تم تعطيله
    
    const handleUpdateUnreadCount = () => {
      // console.log('🔄 تحديث عداد الرسائل غير المقروءة من Header'); // تم تعطيله
      fetchUnreadMessages();
    };

    window.addEventListener('updateUnreadCount', handleUpdateUnreadCount);
    
    return () => {
      // console.log('👂 إيقاف الاستماع لتحديث عداد الرسائل غير المقروءة'); // تم تعطيله
      window.removeEventListener('updateUnreadCount', handleUpdateUnreadCount);
    };
  }, [fetchUnreadMessages]);

  // ساعة رقمية — تحديث كل ثانية، أرقام إنجليزية مع ألوان (ساعات / دقائق / ثوانٍ)
  useEffect(() => {
    const tick = () => setCurrentTime(new Date());
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const hours24 = currentTime.getHours();
  const h = (hours24 % 12 || 12).toString().padStart(2, '0');
  const m = currentTime.getMinutes().toString().padStart(2, '0');
  const s = currentTime.getSeconds().toString().padStart(2, '0');
  const clockLocale = i18n.language.startsWith('ar') ? 'ar-JO' : 'en-US';
  const ampm = i18n.language.startsWith('ar')
    ? (hours24 < 12 ? 'ص' : 'م')
    : (hours24 < 12 ? 'AM' : 'PM');

  const dateStr = currentTime.toLocaleDateString(clockLocale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(i18n.language.startsWith('ar') ? {} : { numberingSystem: 'latn' as const })
  });

  const clockJsx = (
    <span className="font-mono tabular-nums inline-flex items-baseline gap-0.5">
      <span className="text-blue-400 font-semibold">{h}</span>
      <span className="text-slate-400">:</span>
      <span className="text-emerald-400 font-semibold">{m}</span>
      <span className="text-slate-400">:</span>
      <span className="text-amber-400 font-semibold">{s}</span>
      <span className="text-slate-300 font-medium text-[0.65em] uppercase tracking-wider ml-0.5">{ampm}</span>
    </span>
  );

  const renderUserMenuDropdown = () => (
    <div
      className="relative user-menu-container"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <button
        type="button"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="flex items-center space-x-2 space-x-reverse text-slate-100 hover:text-white transition-colors duration-150"
        aria-label={isMenuOpen ? t('header.closeUserMenu') : t('header.openUserMenu')}
        aria-haspopup="true"
        aria-expanded={isMenuOpen}
      >
        <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-primary-200 shrink-0">
          <img
            key={user?.profileImage || 'default'}
            src={user?.profileImage ? getImageUrl(user.profileImage) : getImageUrl('')}
            alt={user?.name || ''}
            loading="lazy"
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = getImageUrl('');
            }}
          />
        </div>
        <span className="hidden sm:block text-sm font-medium max-w-[5rem] truncate">
          {user?.name}
        </span>
        {isMenuOpen ? <X className="w-4 h-4 shrink-0" /> : <Menu className="w-4 h-4 shrink-0" />}
      </button>

      {isMenuOpen && (
        <div
          className="user-menu-dropdown absolute end-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <Link
            to="/profile"
            className="flex items-center space-x-2 space-x-reverse px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={() => setIsMenuOpen(false)}
          >
            <User className="w-4 h-4" />
            <span>{t('nav.profile')}</span>
          </Link>
          <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
          <Link
            to="/profile?tab=won"
            className="flex items-center space-x-2 space-x-reverse px-4 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20"
            onClick={() => setIsMenuOpen(false)}
          >
            <span className="w-4 h-4">🏆</span>
            <span>{t('nav.wonAuctions')}</span>
          </Link>
          <Link
            to="/profile?tab=auctions"
            className="flex items-center space-x-2 space-x-reverse px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            onClick={() => setIsMenuOpen(false)}
          >
            <span className="w-4 h-4">📦</span>
            <span>{t('nav.myAuctions')}</span>
          </Link>
          <Link
            to="/profile?tab=unsold"
            className="flex items-center space-x-2 space-x-reverse px-4 py-2 text-sm text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20"
            onClick={() => setIsMenuOpen(false)}
          >
            <span className="w-4 h-4">❌</span>
            <span>{t('nav.unsold')}</span>
          </Link>
          <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
          {user?.isAdmin && (
            <>
              <Link
                to="/admin"
                className="flex items-center space-x-2 space-x-reverse px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => setIsMenuOpen(false)}
              >
                <Settings className="w-4 h-4" />
                <span>{t('nav.admin')}</span>
              </Link>
              <Link
                to="/banner-management"
                className="flex items-center space-x-2 space-x-reverse px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="w-4 h-4">📢</span>
                <span>{t('nav.bannerMgmt')}</span>
              </Link>
            </>
          )}
          {showCloseApp && (
            <button
              type="button"
              onClick={() => {
                handleCloseApp();
                setIsMenuOpen(false);
              }}
              className="hidden md:flex items-center space-x-2 space-x-reverse w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Power className="w-4 h-4" />
              <span>{t('nav.closeApp')}</span>
            </button>
          )}
          <button
            onClick={() => {
              handleLogout();
              setIsMenuOpen(false);
            }}
            className="flex items-center space-x-2 space-x-reverse w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <LogOut className="w-4 h-4" />
            <span>{t('nav.logout')}</span>
          </button>
        </div>
      )}
    </div>
  );

  const renderSearchSuggestions = () =>
    showSuggestions && (suggestions.length > 0 || isSearching) ? (
      <div
        ref={suggestionsRef}
        className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 py-2 z-[60] overflow-hidden"
      >
        {isSearching && suggestions.length === 0 ? (
          <div className="px-4 py-3 flex items-center gap-2 text-slate-500">
            <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">{t('header.searching')}...</span>
          </div>
        ) : (
          suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                navigate(`/auctions/${s.id}`);
                setShowSuggestions(false);
              }}
              className="w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-start transition-colors"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                  {s.title.split(new RegExp(`(${searchQuery})`, 'gi')).map((part: string, i: number) =>
                    part.toLowerCase() === searchQuery.toLowerCase() ? (
                      <span key={i} className="text-primary-500 bg-primary-50 dark:bg-primary-900/40 rounded-sm px-0.5">{part}</span>
                    ) : part
                  )}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 capitalize">{s.category} • {s.subCategory}</p>
              </div>
              <Search className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 shrink-0" />
            </button>
          ))
        )}
      </div>
    ) : null;

  const closeMobileNav = () => setIsMobileNavOpen(false);

  const mobileNavLinkClass =
    'flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors';

  const renderMobileNavDrawer = () =>
    isMobileNavOpen ? (
      <div className="fixed inset-0 z-[70] md:hidden">
        <button
          type="button"
          className="absolute inset-0 bg-black/50"
          onClick={closeMobileNav}
          aria-label={t('header.closeUserMenu')}
        />
        <nav
          className="absolute inset-y-0 end-0 flex w-72 max-w-[85vw] flex-col bg-white shadow-xl dark:bg-gray-800"
          aria-label={t('header.openUserMenu')}
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
            <span className="font-semibold text-gray-900 dark:text-white">{t('brand')}</span>
            <button
              type="button"
              onClick={closeMobileNav}
              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
              aria-label={t('header.closeUserMenu')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto py-2">
            <Link to="/" className={mobileNavLinkClass} onClick={closeMobileNav}>
              {t('nav.home')}
            </Link>
            <Link to="/auctions" className={mobileNavLinkClass} onClick={closeMobileNav}>
              {t('nav.auctions')}
            </Link>
            <Link to="/about" className={mobileNavLinkClass} onClick={closeMobileNav}>
              {t('nav.about')}
            </Link>
            <Link to="/contact" className={mobileNavLinkClass} onClick={closeMobileNav}>
              {t('nav.contact')}
            </Link>

            <div className="my-2 border-t border-gray-100 dark:border-gray-700" />

            {user ? (
              <>
                <Link to="/profile" className={mobileNavLinkClass} onClick={closeMobileNav}>
                  <User className="h-4 w-4 shrink-0" />
                  <span>{t('nav.profile')}</span>
                </Link>
                <Link to="/messages" className={mobileNavLinkClass} onClick={closeMobileNav}>
                  <MessageSquare className="h-4 w-4 shrink-0" />
                  <span>{t('nav.messages')}</span>
                </Link>
                <Link to="/create-auction" className={mobileNavLinkClass} onClick={closeMobileNav}>
                  <Plus className="h-4 w-4 shrink-0" />
                  <span>{t('nav.createAuction')}</span>
                </Link>
                <Link
                  to="/profile?tab=won"
                  className={`${mobileNavLinkClass} text-green-600 dark:text-green-400`}
                  onClick={closeMobileNav}
                >
                  <span className="w-4 shrink-0 text-center">🏆</span>
                  <span>{t('nav.wonAuctions')}</span>
                </Link>
                <Link
                  to="/profile?tab=auctions"
                  className={`${mobileNavLinkClass} text-blue-600 dark:text-blue-400`}
                  onClick={closeMobileNav}
                >
                  <span className="w-4 shrink-0 text-center">📦</span>
                  <span>{t('nav.myAuctions')}</span>
                </Link>
                <Link
                  to="/profile?tab=unsold"
                  className={`${mobileNavLinkClass} text-orange-600 dark:text-orange-400`}
                  onClick={closeMobileNav}
                >
                  <span className="w-4 shrink-0 text-center">❌</span>
                  <span>{t('nav.unsold')}</span>
                </Link>
                {user.isAdmin && (
                  <>
                    <Link to="/admin" className={mobileNavLinkClass} onClick={closeMobileNav}>
                      <Settings className="h-4 w-4 shrink-0" />
                      <span>{t('nav.admin')}</span>
                    </Link>
                    <Link to="/banner-management" className={mobileNavLinkClass} onClick={closeMobileNav}>
                      <span className="w-4 shrink-0 text-center">📢</span>
                      <span>{t('nav.bannerMgmt')}</span>
                    </Link>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => {
                    handleLogout();
                    closeMobileNav();
                  }}
                  className={`${mobileNavLinkClass} w-full`}
                >
                  <LogOut className="h-4 w-4 shrink-0" />
                  <span>{t('nav.logout')}</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className={mobileNavLinkClass} onClick={closeMobileNav}>
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  className={`${mobileNavLinkClass} font-medium text-primary-600 dark:text-primary-400`}
                  onClick={closeMobileNav}
                >
                  {t('nav.register')}
                </Link>
              </>
            )}
          </div>

          {showCloseApp && (
            <button
              type="button"
              onClick={() => {
                handleCloseApp();
                closeMobileNav();
              }}
              className="flex w-full items-center gap-3 border-t border-gray-200 px-4 py-3 text-sm text-red-600 hover:bg-red-50 dark:border-gray-700 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              <Power className="h-4 w-4 shrink-0" />
              <span>{t('nav.closeApp')}</span>
            </button>
          )}
        </nav>
      </div>
    ) : null;

  return (
    <header className="app-header bg-slate-700 dark:bg-slate-800 shadow-lg border-b border-slate-600 dark:border-slate-700 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto">
        {/* Desktop layout */}
        <div className="hidden md:block px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo - Modern Glassmorphism Circle */}
          <Link
            to="/"
            aria-label={t('nav.home')}
            title={t('nav.home')}
            className="relative flex items-center rounded-full bg-white/10 dark:bg-slate-700/50 p-1 backdrop-blur-md ring-1 ring-white/20 dark:ring-slate-600/50 shadow-lg hover:shadow-primary-500/20 hover:ring-primary-400/60 transition-all duration-500 group"
          >
            <img
              src={BRAND_LOGO_PUBLIC_PATH}
              alt={t('header.logoAlt')}
              className="h-12 w-12 sm:h-14 sm:w-14 object-contain rounded-full group-hover:scale-110 transition-transform duration-500"
            />
            {!isHome && (
              <span className="absolute -bottom-1 -start-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-white ring-2 ring-slate-700 dark:ring-slate-800 lg:hidden">
                <Home className="h-3 w-3" aria-hidden />
              </span>
            )}
          </Link>

          {/* Search Bar + ساعة رقمية */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 items-center max-w-2xl mx-4 lg:mx-8 gap-2">
            <div className="relative shrink-0 flex items-center gap-1 hidden">
              <Globe className="w-4 h-4 text-slate-400 shrink-0" aria-hidden />
              <select
                aria-label={t('header.countryFilterAria')}
                value={headerCountryValue}
                onChange={(e) => applyCountryFilter(e.target.value)}
                className="max-w-[7.5rem] lg:max-w-[11rem] py-2 px-2 bg-slate-600/80 border border-slate-500 rounded-lg text-white text-xs lg:text-sm focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
              >
                <option value="">{t('header.countryAll')}</option>
                {auctionCountries.map((c) => (
                  <option key={c} value={c}>
                    {regionDisplayName(c, i18n.language)} ({c})
                  </option>
                ))}
              </select>
            </div>
            <div className="relative flex-1 min-w-0 search-container">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={t('header.searchPlaceholder')}
                value={searchQuery}
                onFocus={() => searchQuery.trim().length >= 2 && setShowSuggestions(true)}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pr-10 pl-4 py-2 bg-slate-600/80 border border-slate-500 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
              />

              {renderSearchSuggestions()}
            </div>
            <div
              className="shrink-0 flex flex-col items-center justify-center min-w-[6rem] px-3 py-2 rounded-lg bg-slate-600/80 border border-slate-500 text-sm tracking-wide transition-all duration-300 hover:bg-slate-500/80 hover:border-primary-400/50"
              title={currentTime.toLocaleDateString(clockLocale, { dateStyle: 'full' })}
            >
              <span className="text-[0.65rem] text-slate-400 leading-tight mb-0.5 whitespace-nowrap" aria-hidden="true">
                {dateStr}
              </span>
              {clockJsx}
            </div>
          </form>

          {/* Navigation */}
          <div className="flex items-center space-x-4 space-x-reverse">
            {user ? (
              <>
                {/* Add Item Button */}
                <Link
                  to="/create-auction"
                  className="hidden sm:flex items-center space-x-2 space-x-reverse bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-4 py-2 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 font-medium"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('nav.createAuction')}</span>
                </Link>

                {/* زر إنشاء مزاد للموبايل */}
                <Link
                  to="/create-auction"
                  className="sm:hidden bg-gradient-to-r from-green-500 to-emerald-600 text-white p-2 rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-200"
                  aria-label={t('nav.createAuction')}
                >
                  <Plus className="w-5 h-5" />
                </Link>

                {/* Notifications */}
                <div className="relative notification-dropdown-container">
                  <button
                    type="button"
                    onClick={() => {
                      setIsNotificationDropdownOpen(!isNotificationDropdownOpen);
                      if (!isNotificationDropdownOpen) {
                        throttledFetchNotifications();
                        fetchUnreadMessages();
                      }
                    }}
                    className="relative p-2 text-slate-200 hover:text-white transition-colors duration-150"
                    aria-label={t('header.notifications')}
                    aria-haspopup="true"
                    aria-expanded={isNotificationDropdownOpen}
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {/* Notification Dropdown */}
                  {isNotificationDropdownOpen && (
                    <div className="notification-dropdown absolute mt-2 w-80 sm:w-80 md:w-96 lg:w-80 xl:w-80 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50 max-h-96 overflow-y-auto transform -translate-x-1/2 left-1/2 sm:left-0 sm:transform-none">
                      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('header.notifications')}</h3>
                      </div>
                      
                      {notifications.length === 0 ? (
                        <div className="px-4 py-8 text-center text-gray-700 dark:text-gray-300">
                          <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" aria-hidden />
                          <p className="text-sm">{t('header.noNotifications')}</p>
                        </div>
                      ) : (
                        <div className="max-h-80 overflow-y-auto">
                          {notifications.slice(0, 10).map((notification) => {
                            const loc = localizeStoredNotification(
                              {
                                type: notification.type,
                                title: notification.title,
                                message: notification.message,
                                data: notification.data
                              },
                              t,
                              i18n.language
                            );
                            const timeLocale = i18n.language.startsWith('ar') ? 'ar-SA' : 'en-US';
                            return (
                            <div
                              key={String(notification.id ?? '')}
                              className={`px-4 py-3 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                                !notification.read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white break-words">
                                    {loc.title}
                                  </p>
                                  <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 break-words leading-relaxed">
                                    {loc.message}
                                  </p>
                                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                    {notification.createdAt ? new Date(notification.createdAt).toLocaleString(timeLocale) : ''}
                                  </p>
                                </div>
                                <div className="flex items-center space-x-1 space-x-reverse ml-2">
                                  {!notification.read && notification.id != null && (
                                    <button
                                      onClick={() => markAsRead(String(notification.id!))}
                                      className="text-blue-600 hover:text-blue-800 text-xs"
                                      title={t('header.markRead')}
                                      aria-label={t('header.markRead')}
                                    >
                                      ✓
                                    </button>
                                  )}
                                  <button
                                    onClick={() => notification.id != null && deleteNotification(String(notification.id))}
                                    className="text-red-600 hover:text-red-800 text-xs"
                                    title={t('header.deleteNotification')}
                                    aria-label={t('header.deleteNotification')}
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {notifications.length > 0 && (
                        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700">
                          <button
                            type="button"
                            onClick={() => markAllAsRead()}
                            className="text-sm font-medium text-blue-700 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200 transition-colors duration-150"
                            aria-label={t('header.markAllRead')}
                          >
                            {t('header.markAllRead')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Messages */}
                <Link
                  to="/messages"
                  className="relative p-2 text-slate-200 hover:text-white transition-colors duration-150"
                  aria-label={t('nav.messages')}
                >
                  <MessageSquare className="w-5 h-5" />
                  {/* عداد الإشعارات - يظهر دائماً للتجربة */}
                  <span className={`absolute -top-1 -right-1 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold ${
                    unreadMessages > 0 ? 'bg-red-500' : 'bg-gray-400'
                  }`}>
                    {unreadMessages > 99 ? '99+' : unreadMessages}
                  </span>
                  {/* إضافة عنصر للتصحيح */}
                  <span className="sr-only">{t('header.unreadMessagesSr', { count: unreadMessages })}</span>
                </Link>

                {renderUserMenuDropdown()}
              </>
            ) : (
              <div className="flex items-center space-x-4 space-x-reverse">
                {/* زر إنشاء مزاد للزوار */}
                <button
                  onClick={() => navigate('/login')}
                  className="hidden sm:flex items-center space-x-2 space-x-reverse bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-4 py-2 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 font-medium"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('nav.createAuction')}</span>
                </button>
                
                {/* زر إنشاء مزاد للزوار - موبايل */}
                <button
                  onClick={() => navigate('/login')}
                  className="sm:hidden bg-gradient-to-r from-green-500 to-emerald-600 text-white p-2 rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-200"
                  aria-label={t('nav.createAuction')}
                >
                  <Plus className="w-5 h-5" />
                </button>
                
                <Link
                  to="/login"
                  className="text-slate-200 hover:text-white transition-colors"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-400 transition-colors"
                >
                  {t('nav.register')}
                </Link>
              </div>
            )}
          </div>
          <label className="sr-only" htmlFor="oa-header-lang">{t('header.language')}</label>
          <select
            id="oa-header-lang"
            aria-label={t('header.language')}
            value={(i18n.language || 'ar').split('-')[0]}
            onChange={(e) => {
              void i18n.changeLanguage(e.target.value);
            }}
            className="max-w-[6.5rem] sm:max-w-[7.5rem] py-2 px-1.5 ml-2 rounded-lg bg-slate-600/90 border border-slate-500 text-white text-xs sm:text-sm focus:ring-2 focus:ring-primary-400"
          >
            {SUPPORTED_LANGS.map((lng) => (
              <option key={lng} value={lng}>
                {t(`languages.${lng}`)}
              </option>
            ))}
          </select>
          <button
            onClick={toggleDarkMode}
            className="ml-2 p-2 rounded-full bg-slate-600 hover:bg-slate-500 transition-colors"
            title={darkMode ? t('header.lightMode') : t('header.darkMode')}
          >
            {darkMode ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-slate-200" />}
          </button>
        </div>
        </div>

        {/* Mobile layout */}
        <div className="md:hidden space-y-0.5 px-2 pb-0 pt-0.5">
          <form
            onSubmit={handleSearch}
            className="mobile-header-toolbar flex items-center gap-1 min-h-8 w-full"
            title={currentTime.toLocaleDateString(clockLocale, { dateStyle: 'full' })}
          >
            <Link
              to="/"
              aria-label={t('nav.home')}
              title={t('nav.home')}
              className="relative flex shrink-0 items-center rounded-full bg-white/10 p-0.5 ring-1 ring-white/20 after:absolute after:-inset-x-2 after:-inset-y-1 after:content-['']"
            >
              <img
                src={BRAND_LOGO_PUBLIC_PATH}
                alt={t('header.logoAlt')}
                className="h-7 w-7 object-contain rounded-full"
              />
              {!isHome && (
                <span className="absolute -bottom-1 -start-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary-500 text-white ring-2 ring-slate-700">
                  <Home className="h-2 w-2" aria-hidden />
                </span>
              )}
            </Link>

            <span className="shrink-0 scale-90 origin-center text-[10px] leading-none hidden sm:block">
              {clockJsx}
            </span>

            <div className="relative search-container min-w-0 flex-1 max-w-[140px] sm:max-w-none mx-1">
              <Search className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchInputRef}
                type="search"
                placeholder={t('header.searchPlaceholder')}
                value={searchQuery}
                onFocus={() => searchQuery.trim().length >= 2 && setShowSuggestions(true)}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="mobile-header-search h-7 w-full rounded-md border border-slate-500 bg-slate-600/80 py-0.5 pl-2 pr-7 text-[10px] sm:text-[11px] text-white placeholder-slate-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
              />
              {renderSearchSuggestions()}
            </div>

            <div className="hidden">
              <Globe className="w-3 h-3 text-slate-400 shrink-0" aria-hidden />
              <select
                aria-label={t('header.countryFilterAria')}
                value={headerCountryValue}
                onChange={(e) => applyCountryFilter(e.target.value)}
                className="mobile-header-row h-7 min-w-[3.25rem] max-w-[4.25rem] shrink-0 rounded-md border border-slate-500 bg-slate-600/90 px-1 text-[10px] text-white focus:ring-1 focus:ring-primary-400"
              >
                <option value="">{i18n.language.startsWith('ar') ? 'الكل' : 'All'}</option>
                {auctionCountries.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <label className="sr-only" htmlFor="oa-mobile-lang">{t('header.language')}</label>
            <select
              id="oa-mobile-lang"
              aria-label={t('header.language')}
              value={(i18n.language || 'ar').split('-')[0]}
              onChange={(e) => {
                void i18n.changeLanguage(e.target.value);
              }}
              className="mobile-header-row h-7 shrink-0 rounded-md border border-slate-500 bg-slate-600/90 px-0.5 text-[10px] font-bold text-white focus:ring-1 focus:ring-primary-400"
            >
              {SUPPORTED_LANGS.map((lng) => (
                <option key={lng} value={lng}>
                  {lng === 'ar' ? 'ع' : 'EN'}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={toggleDarkMode}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-600 hover:bg-slate-500"
              title={darkMode ? t('header.lightMode') : t('header.darkMode')}
              aria-label={darkMode ? t('header.lightMode') : t('header.darkMode')}
            >
              {darkMode ? <Sun className="w-3.5 h-3.5 text-yellow-400" /> : <Moon className="w-3.5 h-3.5 text-slate-200" />}
            </button>

            {!authLoading && !user && (
              <Link
                to="/login"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-500 text-white transition-colors hover:bg-primary-400"
                title={t('nav.login')}
                aria-label={t('nav.login')}
              >
                <LogIn className="w-3.5 h-3.5 rtl-flip" aria-hidden />
              </Link>
            )}

            <button
              type="button"
              onClick={() => setIsMobileNavOpen((open) => !open)}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-600 hover:bg-slate-500 text-slate-100"
              aria-label={isMobileNavOpen ? t('header.closeUserMenu') : t('header.openUserMenu')}
              aria-expanded={isMobileNavOpen}
            >
              {isMobileNavOpen ? <X className="w-3.5 h-3.5" /> : <Menu className="w-3.5 h-3.5" />}
            </button>
          </form>

          <div className="mt-1 mb-1">
            <MobilePlatformAdTicker className="w-screen -mx-2 h-7" />
          </div>
        </div>
      </div>
      
      {renderMobileNavDrawer()}

      {/* Notification Permission Request */}
      <NotificationPermissionRequest />
    </header>
  );
};

export default Header;
